import { Box, Button, Typography } from "@outschool/backpack";
import { Timetable } from "@outschool/gql-backend-generated";
import {
  ClassScheduleRequest_ActivityFragment,
  CurrentUserQueryQuery,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { addParamsToUrl, onboardingQuizPath } from "@outschool/routes";
import * as Time from "@outschool/time";
import { DEFAULT_TIMETABLE, timetableHasSelection } from "@outschool/timetable";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  ApolloError,
  gql,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { AuthTrigger, useLoginFlowContext } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  ModalActionButtons,
  Space,
} from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import { cleanTypeNameField } from "../../shared/client/ApolloClient";
import * as Env from "../../shared/Env";
import { createClassScheduleRequestMutation } from "../queries/ClassScheduleRequestMutations";
// @ts-ignore - JSX import
import { lastClassScheduleRequestQuery } from "../queries/ClassScheduleRequestQueries";
import EditableTimetable from "./EditableTimetable";
import ErrorMessage from "./ErrorMessage";
import PageModal from "./PageModal";
import ShareContainer from "./sharing/ShareContainer";

interface ClassScheduleRequestProps {
  activity: ClassScheduleRequest_ActivityFragment;
  user: CurrentUserQueryQuery["currentUser"];
  userTimeZone: string;
  isLoggedIn: boolean;
  renderButton?: (
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void,
  ) => React.ReactElement;
  onHandleClose?: () => void;
}

function ClassScheduleRequest({
  activity,
  user,
  userTimeZone,
  isLoggedIn,
  renderButton,
  onHandleClose,
}: ClassScheduleRequestProps) {
  const location = useLocation();
  const { t } = useTranslation("client\\components\\ClassScheduleRequest");
  const [open, setOpen] = React.useState(false);
  const { enterLoginFlow } = useLoginFlowContext();

  if (!renderButton) {
    renderButton = open => (
      <TrackedButton
        onClick={open}
        trackingName={"Request a time clicked"}
        trackingUniqueId={activity?.uid}
        trackViews={true}
      >
        {t`Request Another Time`}
      </TrackedButton>
    );
  }

  React.useEffect(() => {
    if (isLoggedIn && location.query?.openCSR) {
      setOpen(true);
    }
  }, [isLoggedIn, location.query]);

  const handleOpenClick = React.useCallback(() => {
    if (!isLoggedIn) {
      const redirectUrlAfterLoginFlow = addParamsToUrl(
        location.pathname + location.search,
        { openCSR: true },
      );

      enterLoginFlow({
        authTrigger: AuthTrigger.REQUEST_TIME,
        newUrlAfterSignUp: addParamsToUrl(onboardingQuizPath(), {
          requestTime: true,
          landingPage: redirectUrlAfterLoginFlow,
        }),
        newUrlAfterLogin: redirectUrlAfterLoginFlow,
        shouldNavigateOverwrite: false,
      });
      return;
    }
    setOpen(true);
  }, [enterLoginFlow, isLoggedIn, location]);

  return (
    <React.Fragment>
      {renderButton(handleOpenClick)}
      <ClassScheduleRequestModal
        activity={activity}
        user={user}
        userTimeZone={userTimeZone}
        open={open}
        setOpen={setOpen}
        onHandleClose={onHandleClose}
      />
    </React.Fragment>
  );
}

export function ClassScheduleRequestModal({
  activity,
  user,
  userTimeZone,
  open,
  setOpen,
  onHandleClose = () => {},
}: {
  activity: ClassScheduleRequest_ActivityFragment;
  user: CurrentUserQueryQuery["currentUser"];
  userTimeZone: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onHandleClose?: () => void;
}) {
  const rHistory = useHistory();
  const location = useLocation();
  const trackEvent = useTrackEvent();
  const [requestedTimetable, setRequestedTimetable] = React.useState(null);

  const [createClassScheduleRequest, { error, loading: sending }] = useMutation(
    createClassScheduleRequestMutation,
  );

  const handleCancel = React.useCallback(() => {
    setOpen(false);
    setRequestedTimetable(null);

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("openCSR");
    rHistory.replace(location.pathname + "?" + searchParams.toString());
  }, [setOpen, rHistory, location]);

  const handleClose = () => {
    handleCancel();
    onHandleClose();
  };

  const handleSubmit = React.useCallback(
    async ({ timetable, message }: $TSFixMe) => {
      const cleanTimetable = cleanTypeNameField(_.cloneDeep(timetable));
      await createClassScheduleRequest({
        variables: {
          activityUid: activity.uid,
          timetable: cleanTimetable,
          timeZone: userTimeZone,
          message,
        },
      });
      // @ts-ignore TS(2345): Argument of type 'ObjectWithoutTypename<any>' is n... Remove this comment to see the full error message
      setRequestedTimetable(cleanTimetable);
      trackEvent("Class schedule request", {
        activity_uid: activity.uid,
      });
    },
    [activity.uid, createClassScheduleRequest, userTimeZone, trackEvent],
  );

  const { data, loading } = useQueryWithPreviousData(
    lastClassScheduleRequestQuery,
  );

  if (loading || !user || !open) {
    return null;
  }

  const timetable =
    data && data.currentUser && data.currentUser.lastClassScheduleRequest
      ? data.currentUser.lastClassScheduleRequest.timetable
      : DEFAULT_TIMETABLE;

  return (
    <PageModal open={open} onClose={handleClose}>
      {requestedTimetable ? (
        <PostClassScheduleRequestModalContent
          activity={activity}
          user={user}
          userTimeZone={userTimeZone}
          timetable={requestedTimetable}
          onCancel={handleCancel}
        />
      ) : (
        <RequestForm
          // @ts-ignore TS(2322): Type 'ApolloError | undefined' is not assignable t... Remove this comment to see the full error message
          error={error}
          initialTimetable={timetable}
          user={user}
          sending={sending}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      )}
    </PageModal>
  );
}

function readableTimeRange(range: string): string {
  // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
  return {
    f8_12: "8am-12pm",
    f12_4: "12-4pm",
    f4_8: "4-8pm",
  }[range];
}

interface PostClassScheduleRequestModalContentProps {
  activity: ClassScheduleRequest_ActivityFragment;
  user: CurrentUserQueryQuery["currentUser"];
  userTimeZone: string;
  timetable: Timetable;
  onCancel(): void;
}

function PostClassScheduleRequestModalContent({
  activity,
  user,
  userTimeZone,
  timetable,
  onCancel,
}: PostClassScheduleRequestModalContentProps) {
  const { t } = useTranslation("client\\components\\ClassScheduleRequest");
  const isMobile = Screen.useIsMobile();

  const timeZone = Time.timeZoneName(userTimeZone);

  const timetableSummary = ["f8_12", "f12_4", "f4_8"]
    .map(range => ({
      range: readableTimeRange(range),
      days: Object.keys(timetable)
        //@ts-ignore
        .filter(day => timetable[day][range])
        .map(_.capitalize)
        .join(", "),
    }))
    .reduce((acc, { range, days }) => {
      if (days.length === 0) {
        return acc;
      }
      return acc + `${days} ${range} ${timeZone}\n`;
    }, "");

  const hasSelection = timetableHasSelection(timetable);

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Typography variant="h2">{t`Schedule Request Received!`}</Typography>
      <Space y="small" />
      {hasSelection && (
        <Box>
          <Box
            sx={{
              maxWidth: "350px",
              margin: "0 auto",
            }}
          >{t`We’ll contact you if the teacher creates new classes at these times:`}</Box>
          <Box
            sx={{
              backgroundColor: "#f8f8f9",
              border: "1px solid",
              borderColor: "#f1f2f3",
              borderRadius: "8px",
              color: "grey.500",
              marginY: "1em",
              width: "100%",
            }}
          >
            <pre>{timetableSummary}</pre>
          </Box>
        </Box>
      )}
      <Typography
        variant={isMobile ? "h4" : "h3"}
        sx={{
          marginY: "1em",
        }}
      >
        {t`Teachers are more likely to create a class if it’s requested by multiple parents.`}
      </Typography>
      <ShareContainer
        activity={activity}
        user={user}
        onEmailShareClose={onCancel}
      />
      <Button
        variant="contained"
        onClick={onCancel}
        sx={{
          marginTop: "1em",
        }}
      >
        {t("Close")}
      </Button>
    </Box>
  );
}

interface RequestFormProps {
  error: ApolloError;
  initialTimetable: Timetable;
  user: CurrentUserQueryQuery["currentUser"];
  sending: boolean;
  questionLink: { url: string; message: string };
  onSubmit(props: { timetable: Timetable; message: string }): void;
  onCancel(): void;
}

function RequestForm({
  error,
  initialTimetable,
  user,
  sending,
  onSubmit,
  onCancel,
}: RequestFormProps) {
  const { t } = useTranslation("client\\components\\ClassScheduleRequest");
  const [timetable, setTimeTable] = React.useState(initialTimetable);
  const [message, setMessage] = React.useState("");
  const isMobile = Screen.useIsMobile();

  return (
    <Box
      flex
      sx={{
        alignItems: "stretch",
        flexDirection: "column",
      }}
    >
      <Typography variant="h3">{t`Request another time`}</Typography>
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
      <Space y="medium" />
      <Box>{t`Send a message to the teacher telling them what times would work best for you.`}</Box>
      <Space y={"medium"} />
      <EditableTimetable
        timetable={timetable}
        onChange={(timetable: $TSFixMe) => setTimeTable(timetable)}
        disabled={sending}
      />
      <Space y="large" />
      <textarea
        value={message}
        placeholder={t`Write your message here`}
        onChange={e => setMessage(e.currentTarget.value)}
        disabled={sending}
        // eslint-disable-next-line
        autoFocus={!isMobile}
      />
      <Space y="small" />
      <Box>
        {/* @ts-ignore TS(2531): Object is possibly 'null'. */}
        {t("Responses will go to {{userEmail}}", { userEmail: user.email })}
      </Box>
      <Space y="medium" />
      <ModalActionButtons
        primaryLabel={t`Send Request`}
        onPrimary={() => onSubmit({ timetable, message })}
        onCancel={onCancel}
        disabled={sending}
      />
    </Box>
  );
}

ClassScheduleRequest.fragments = {
  activity: gql`
    fragment ClassScheduleRequest_activity on Activity {
      uid
      ...ShareContainer_activity
    }
    ${ShareContainer.fragments.activity}
  `,
};

export default ClassScheduleRequest;
